// Whether a lane of ours actually works: is anything feeding it, and does
// following it forward arrive anywhere that matters.
//
// **Extracted because three callers need the same two answers**, and they sit
// on opposite sides of an import edge. `uselessBelts` (opponent.ts) has asked
// them since it shipped; `evaluate` now prices dead track with them; and
// `inFlightValue` (factory.ts) needs them to stop calling a unit "on its way to
// a forge" when it is parked at the head of a lane that ends in open ground.
// `opponent.ts` already imports `factory.ts`, so the shared answer cannot live
// in either.
//
// **This is a question about *now*, and it has no memory.** A lane half-built
// toward a foundry reads as dead, because it is — what makes it worth building
// is a plan, and a plan is `ai/tasks.ts`'s business, not this file's. The
// consequence to keep in mind: everything here is a *weight*, never a gate. A
// hard rule against dead track would refuse the second part of every lane
// anybody ever laid.

import { areEnemies, hasCell, pieceAt } from "src/engine/rules/position";
import { type Color, DIR_OFFSET, pieceColor, ROOK_DIRS } from "src/engine/rules/board";
import {
  beltDirAt,
  beltIsLive,
  beltOwnerAt,
  cargoTotal,
  hasStructure,
  liveSquares
} from "src/engine/rules/industry/state";
import { laneDelivers } from "src/engine/rules/industry/belts";
import type { GameState } from "src/engine/rules/commands";
import { WEIGHTS, type Weights } from "./tuning";

/**
 * Would anything of ours put material onto this part?
 *
 * A piece beside it counts even when it is holding nothing — a lane laid ahead
 * of the ore is a lane somebody meant, and lifting it because it is empty this
 * turn is how a half-built run gets retracted from under its builder.
 */
export function laneFed(
  state: GameState,
  sq: number,
  side: Color,
  /**
   * Parts already condemned this turn, which no longer count as feeders.
   *
   * `uselessBelts` retracts a dead run by walking it: once its head is going,
   * the part behind that head is fed by nothing either, and so on. Without
   * this the answer is "fed" for every part but the head — by a part that is
   * itself about to be lifted — which is why a six-part spur used to take six
   * turns to disappear however high the removal cap was set.
   */
  gone: ReadonlySet<number> = new Set()
): boolean {
  for (const offset of ROOK_DIRS) {
    const adj = sq + offset;
    if (!hasCell(state.position, adj) && !hasStructure(state.industry, adj)) continue;
    if (gone.has(adj)) continue;

    // A part pointing into this one.
    if (beltIsLive(state.industry, adj, side)) {
      const facing = beltDirAt(state.industry, adj);
      if (facing !== -1 && adj + DIR_OFFSET[facing] === sq) return true;
    }
    // A miner or a carrier that might load onto it.
    const piece = pieceAt(state.position, adj);
    if (piece !== 0 && !areEnemies(state.position, side, pieceColor(piece))) return true;
    if (hasStructure(state.industry, adj)) return true;
  }
  return false;
}

/**
 * Does following this part forward arrive anywhere that matters?
 *
 * **The rules' own answer** (`belts.ts`), because routing now asks the same
 * question when it chooses which lane to load — and two definitions of "does
 * this lane work" would let the opponent value a network the reducer routes
 * differently. A ring answers false: it is running, it is fed, and it delivers
 * nothing.
 *
 * The ring is still safe from `uselessBelts`, and by a better mechanism than
 * the old special case — every part of a ring is fed by the one behind it, so
 * `laneIsOrphan` is false for all of them without anybody naming loops.
 */
export const laneReaches = (
  state: GameState,
  sq: number,
  side: Color,
  w: Weights = WEIGHTS
): boolean => laneDelivers(state.position, state.industry, sq, side, w.chaseLimit);

/**
 * An orphan: nothing feeds it *and* it arrives nowhere.
 *
 * The strictest reading, and the one `uselessBelts` demolishes on — shared so
 * that "dead" has one definition. Both clauses, because either alone describes
 * a lane somebody is halfway through building.
 */
export const laneIsOrphan = (
  state: GameState,
  sq: number,
  side: Color,
  w: Weights = WEIGHTS,
  /** Parts already condemned this turn. See `laneFed`. */
  gone: ReadonlySet<number> = new Set()
): boolean => !laneFed(state, sq, side, gone) && !laneReaches(state, sq, side, w);

/**
 * Is this part of ours earning nothing? Two populations, and the difference
 * between them is what stops this term punishing construction.
 *
 *  - **An orphan.** Nothing feeds it and it goes nowhere, so no material will
 *    ever arrive on it and none could leave if it did.
 *  - **A loaded dead end.** It arrives nowhere *and there is cargo sitting on
 *    it*. This is the one worth having: `advanceBelts` leaves cargo on a
 *    terminal part rather than spilling it, so material that rode onto a spur
 *    into open ground is out of the economy until somebody finishes or
 *    re-points the lane — and `inFlightValue` was scoring it as though it were
 *    on its way to a forge.
 *
 * **Cargo is the discriminator, and that is the whole design of this term.**
 * An empty part that reaches nothing is not charged, because that describes
 * every lane on the turn it is laid — a run is built from its tail and arrives
 * nowhere until the last part goes down. Once material is *parked* on it, it
 * has stopped being a lane under construction and started being a hole.
 */
export const laneIsDead = (
  state: GameState,
  sq: number,
  side: Color,
  w: Weights = WEIGHTS
): boolean =>
  laneIsOrphan(state, sq, side, w) ||
  (cargoTotal(state.industry, sq) > 0 && !laneReaches(state, sq, side, w));

/** Parts of `side`'s that are earning nothing, and the cargo parked on them. */
export function deadTrack(
  state: GameState,
  side: Color,
  w: Weights = WEIGHTS
): { parts: number; stranded: number } {
  let parts = 0;
  let stranded = 0;
  for (const sq of liveSquares(state.industry.beltDir)) {
    if (beltOwnerAt(state.industry, sq) !== side) continue;
    if (!laneIsDead(state, sq, side, w)) continue;
    parts++;
    stranded += cargoTotal(state.industry, sq);
  }
  return { parts, stranded };
}

